import { useEffect, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useInbox } from '@/hooks/useInbox';
import { useToast } from '@/components/Toast';
import { useAuth } from '@/lib/auth';
import { SeverityBadge, StatusBadge, FloorBadge } from '@/components/badges';
import { getPcs, getRooms, shortId } from '@/services/refdata';
import { claimIncident } from '@/services/alerts';
import { relativeTime } from '@/lib/format';
import type { IncidentTriage } from '@/types';

export function InboxPage() {
  const toast = useToast();
  const { developer } = useAuth();
  const [rooms, setRooms] = useState<Map<string, string>>(new Map());
  const [pcs, setPcs] = useState<Map<string, string>>(new Map());
  const [floorOnly, setFloorOnly] = useState(false);

  const { items, loading } = useInbox(undefined, (t) =>
    toast(`🚨 ${t.message ?? t.category ?? 'Developer required'}`, 'error'));

  useEffect(() => {
    getRooms().then(setRooms).catch(() => {});
    getPcs().then(setPcs).catch(() => {});
  }, []);

  const shown = floorOnly ? items.filter((i) => i.is_floor) : items;

  async function claim(e: MouseEvent, t: IncidentTriage) {
    e.preventDefault();
    try {
      const r = await claimIncident(t.incident_id);
      if (r.ok) toast('You are now investigating this incident', 'success');
      else toast('Already claimed by someone else', 'info');
    } catch (err: any) {
      toast(err.message ?? 'Failed to claim', 'error');
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Inbox</h1>
        <label className="checkline">
          <input type="checkbox" checked={floorOnly} onChange={(e) => setFloorOnly(e.target.checked)} />
          Dev-required only
        </label>
      </div>

      {loading && items.length === 0 && <div className="muted">Loading…</div>}
      {!loading && shown.length === 0 && <div className="empty">🎉 Nothing needs your attention.</div>}

      <div className="list">
        {shown.map((t) => (
          <Link key={t.incident_id} to={`/incident/${t.incident_id}`} className={`row ${t.is_floor ? 'row-floor' : ''}`}>
            <div className="row-main">
              <div className="row-badges">
                <SeverityBadge severity={t.severity} />
                {t.is_floor && <FloorBadge />}
                <StatusBadge status={t.status} />
                <span className="chip">{t.incident_type}</span>
                {t.category && <span className="chip chip-dim">{t.category}</span>}
              </div>
              <div className="row-msg">{t.message ?? '(no message)'}</div>
              <div className="row-meta muted">
                {rooms.get(t.room_id ?? '') ?? shortId(t.room_id)} · PC {pcs.get(t.pc_instance_id ?? '') ?? shortId(t.pc_instance_id)} · {relativeTime(t.occurred_at)}
                {t.claimed_by && <> · claimed{t.claimed_by === developer?.id ? ' by you' : ''}</>}
              </div>
            </div>
            <div className="row-actions">
              {!t.claimed_by && t.status === 'new' && (
                <button className="btn btn-sm" onClick={(e) => claim(e, t)}>I'm on it</button>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
